/**
 * shiftUtils — pure helpers for shift time strings, weather codes,
 * and the open-meteo fetch.
 *
 * No React, no store access — fully tree-shakable, easy to unit test.
 */

/** Fair Oaks Mall coordinates — used as the default weather origin. */
export const FOM_LAT = 38.857
export const FOM_LON = -77.357

/** Map a shift name to its display time range. */
export function getShiftTime(shift: string): string {
  if (shift === 'Morning') return '07:00 - 15:00'
  if (shift === 'Evening') return '15:00 - 23:00'
  return '23:00 - 07:00'
}

/** Convert an open-meteo WMO weather code into a friendly condition string. */
export function wmoToCondition(code: number): string {
  if (code === 0) return 'Clear'
  if (code <= 3) return ['', 'Mainly Clear', 'Partly Cloudy', 'Overcast'][code]
  if (code <= 48) return 'Foggy'
  if (code <= 55) return 'Drizzle'
  if (code <= 65) return 'Rain'
  if (code <= 75) return 'Snow'
  if (code <= 82) return 'Rain Showers'
  if (code === 95) return 'Thunderstorm'
  return 'Unknown'
}

export interface CurrentWeather {
  temp: string
  conditions: string
}

/**
 * Fetch the current weather for the supplied coordinates from open-meteo.
 * Returns a {temp, conditions} object using Fahrenheit and the friendly
 * condition string.
 *
 * Throws on network or parse error so callers can show their own UI.
 */
export async function fetchCurrentWeather(
  lat: number = FOM_LAT,
  lon: number = FOM_LON,
): Promise<CurrentWeather> {
  const url =
    `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
    `&current=temperature_2m,weathercode&temperature_unit=fahrenheit` +
    `&timezone=America%2FNew_York`
  const res = await fetch(url)
  const data = (await res.json()) as {
    current: { temperature_2m: number; weathercode: number }
  }
  return {
    temp: `${Math.round(data.current.temperature_2m)}°F`,
    conditions: wmoToCondition(data.current.weathercode),
  }
}
